import React from 'react';
import styled from 'styled-components';
import {Button, Input, message, Modal, Table} from 'antd';
import {getNewOutlet, getOutlet, useOutlet} from 'reconnect.js';
import * as Jstorage from 'rev.sdk.js/Actions/JStorage';
import {ModalCustomStyle} from '../../AppContext/customStyledCss';
import {
  getUserStateLabel,
  getUserSubstateLabel,
  getUserTypeLabel,
} from '../../Utils/UserUtil';
import {title} from 'process';

const SEARCH_MODAL_OUTLET = 'admin-search-user-jstorage-modal';
const DEFAULT_PAGINATION = {
  total: 0,
  pageSize: 3,
  current: 1,
};

function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
}

function buildNameRegexKeyword(keyword) {
  const trimmed = (keyword || '').trim();
  if (!trimmed.includes('_')) {
    return trimmed;
  }

  const tokens = trimmed.split(/_+/).map((item) => escapeRegex(item));
  return `^${tokens.join('.*')}$`;
}

export default function AdminSelectUserJStorageWidget(props) {
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [fetching, setFetching] = React.useState(false);
  const readonly = props.readonly === true || props.readOnly === true;

  React.useEffect(() => {
    let isActive = true;

    async function fetchSelectedUser() {
      if (!props.value) {
        setSelectedUser(null);
        return;
      }

      setFetching(true);
      try {
        const profile = await Jstorage.fetchOneDocument('user_profile', {
          owner: props.value,
        });
        if (isActive) {
          setSelectedUser(profile || null);
        }
      } catch (err) {
        if (isActive) {
          setSelectedUser(null);
        }
      } finally {
        if (isActive) {
          setFetching(false);
        }
      }
    }

    fetchSelectedUser();

    return () => {
      isActive = false;
    };
  }, [props.value]);

  return (
    <Wrapper>
      <div className="info">
        {fetching ? (
          '設定中...'
        ) : !props.value ? (
          '尚未設定會員資料'
        ) : !selectedUser ? (
          props.value
        ) : (
          <div>
            <Button
              type="link"
              title="查看詳情"
              size="small"
              style={{
                maxWidth: 400,
                width: '100%',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                paddingLeft: 0,
                fontSize: '0.9rem',
                fontWeight: '400',
              }}
              href={`/admin/users/?action=detail&id=${selectedUser.id}`}>
              {selectedUser.username} {selectedUser.name}{' '}
              {getUserStateLabel(selectedUser.state)}{' '}
              {getUserSubstateLabel(selectedUser.substate)}{' '}
              {getUserTypeLabel(selectedUser.user_type)}
            </Button>
            <div style={{fontSize: '0.85rem'}}>手機：{selectedUser.phone}</div>
            <div style={{fontSize: '0.85rem'}}>信箱：{selectedUser.email}</div>
            <div style={{fontSize: '0.85rem'}}>
              備用信箱：
              {selectedUser.EMAIL2 || '無'}
            </div>
          </div>
        )}
      </div>

      {!readonly && (
        <div>
          <Button
            onClick={() => {
              props.onChange('');
              setSelectedUser(null);
            }}
            style={{margin: 5}}>
            清除
          </Button>

          <Button
            type="primary"
            style={{margin: 5}}
            onClick={() =>
              showAdminSearchUserJStorageModal({
                onChange: (owner) => {
                  props.onChange(owner);
                },
              })
            }>
            搜尋
          </Button>
        </div>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--adminBorderColor);
  border-radius: 4px;
  padding: 15px;

  & > .info {
    margin-right: 15px;
  }
`;

getNewOutlet(SEARCH_MODAL_OUTLET, null, {autoDelete: false});

export function showAdminSearchUserJStorageModal(data) {
  getOutlet(SEARCH_MODAL_OUTLET).update(data);
}

async function fetchRecords(keyword, paging = {offset: 0, limit: 5}) {
  try {
    let nextProfiles = [];
    const nextKeyword = (keyword || '').trim();
    const nameKeyword = buildNameRegexKeyword(nextKeyword);

    const query = nextKeyword
      ? {
          $or: [
            {id_card_number: {$regex: nextKeyword}},
            {name: {$regex: nameKeyword}},
            {LAWYER_NO: {$regex: nextKeyword}},
            {birthday: {$regex: nextKeyword}},
            {phone: {$regex: nextKeyword}},
            {email: {$regex: nextKeyword}},
            {EMAIL2: {$regex: nextKeyword}},
          ],
        }
      : {};

    const {results: profiles, total} = await Jstorage.fetchDocuments(
      'user_profile',
      query,
      null,
      paging,
    );

    const privateProfiles = await Jstorage.fetchAllDocuments(
      'private_profile',
      {
        owner: {$in: profiles.map((upr) => upr.owner)},
      },
    );

    for (let record of profiles) {
      const targetPrivateProfile = privateProfiles.find(
        (ppr) => ppr.owner === record.owner,
      );
      record = {
        ...record,
        points: targetPrivateProfile?.points || 0,
        provider: targetPrivateProfile?.provider || record?.provider || '',
      };
      nextProfiles = [...nextProfiles, record];
    }

    return {total, results: nextProfiles};
  } catch (err) {
    message.error('發生錯誤。');
    return {total: 0, results: []};
  }
}

export function AdminSearchUserJStorageModal() {
  const [data, setData] = useOutlet(SEARCH_MODAL_OUTLET);
  const [companies] = useOutlet('companies');
  const [keyword, setKeyword] = React.useState(null);
  const [records, setRecords] = React.useState(null);
  const [isFetchingRecords, setIsFetchingRecords] = React.useState(false);
  const [pagination, setPagination] = React.useState(DEFAULT_PAGINATION);

  async function runFetchRecords(nextPaging) {
    setIsFetchingRecords(true);
    try {
      const {total, results} = await fetchRecords(keyword, nextPaging);
      setRecords(results);
      const current = Math.floor(nextPaging.offset / nextPaging.limit) + 1;
      setPagination((prev) => ({
        ...prev,
        current,
        pageSize: nextPaging.limit,
        total,
      }));
    } finally {
      setIsFetchingRecords(false);
    }
  }

  const columns = [
    {
      key: 'username',
      title: '身分證字號',
      dataIndex: 'username',
      width: 120,
    },
    {
      key: 'name',
      title: '姓名',
      dataIndex: 'name',
      width: 120,
    },
    {
      key: 'info',
      title: '基本資訊',
      dataIndex: 'info',
      render: (_, r) => {
        return (
          <div>
            <div>
              {(r.birthday || '').split('T')[0]} {getUserStateLabel(r.state)}{' '}
              {getUserSubstateLabel(r.substate)} {r.LAWYER_NO}
            </div>
            <div>手機：{r.phone}</div>
            <div>信箱：{r.email}</div>
            <div>備用信箱：{r.EMAIL2 || '無'}</div>
          </div>
        );
      },
    },
    {
      key: 'company_current',
      title: '現在事務所',
      dataIndex: 'company_current',
      render: (_, r) => {
        const cct = r.company_current
          ? companies.find((c) => c.id === r.company_current)
          : {};

        return (
          <div>
            <div>{cct.CONAME || ''}</div>
            <div>
              {r.company_zip_code || cct.address_zip_code}
              {(r.company_city || cct.address_city || '').replace('-', '')}
              {r.company_detail || cct.address_detail}
            </div>
            <div>
              電話：{r.company_phone || cct.COPHONE1 || ''} / 傳真：
              {r.company_fax || cct.COFAX1 || ''}
            </div>
          </div>
        );
      },
    },
    {
      key: 'owner',
      title: '',
      dataIndex: 'owner',
      width: 0,
      render: () => null,
    },
    {
      key: '__action',
      title: '',
      dataIndex: '__action',
      width: 120,
      render: (_, item) => {
        return (
          <Button
            size="small"
            disabled={isFetchingRecords}
            onClick={() => {
              data.onChange(item.owner);
              onClose();
            }}>
            選擇
          </Button>
        );
      },
    },
  ];

  function onClose() {
    setKeyword(null);
    setRecords(null);
    setPagination(DEFAULT_PAGINATION);
    setData(null);
  }

  return (
    <Modal
      visible={!!data}
      onCancel={onClose}
      footer={null}
      width={1100}
      closable={false}
      maskClosable={true}
      destroyOnClose={true}>
      <ModalWrapper>
        <Input.Search
          style={{marginBottom: 10}}
          placeholder="搜尋身分證字號、姓名_、序號、手機、信箱。"
          allowClear
          loading={isFetchingRecords}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={async () => {
            await runFetchRecords({
              offset: 0,
              limit: pagination.pageSize,
            });
          }}
        />

        <Table
          style={{marginBottom: 10}}
          loading={isFetchingRecords}
          scroll={{x: 500, y: 430}}
          pagination={{
            ...pagination,
            size: 'small',
            showTotal: (total) => `共 ${total} 筆`,
            onChange: async (page, pageSize) => {
              await runFetchRecords({
                offset: (page - 1) * pageSize,
                limit: pageSize,
              });
            },
          }}
          columns={columns}
          dataSource={records}
        />
      </ModalWrapper>
    </Modal>
  );
}

const ModalWrapper = styled(ModalCustomStyle)``;
